import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/router'
import { useAppSelector } from '@/redux/core'
import { selectIsAuthenticated } from '@/redux/auth'
import { selectDisplayName, selectProfile } from '@/redux/profile'
import { SidebarProfileProps } from './SidebarProfile.types'

/** Resolves who is signed in and where the row navigates. */
export function useSidebarProfileController({ onNavigate }: SidebarProfileProps) {
  const navigate = useNavigate()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const profile = useAppSelector(selectProfile)
  const displayName = useAppSelector(selectDisplayName)

  const onOpen = useCallback(() => {
    navigate(isAuthenticated ? ROUTES.PROFILE : ROUTES.LOGIN)
    onNavigate?.()
  }, [navigate, isAuthenticated, onNavigate])

  return {
    isAuthenticated,
    avatarUrl: profile.avatarUrl,
    email: profile.email,
    displayName,
    initials: getInitials(displayName || profile.email),
    onOpen,
  }
}

/** First and last initial, falling back to a single glyph for one-word names. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)

  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()

  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}
